import createModule from "../createModule"
import view from "./view"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSelectedModelId, getShowNotHeld, getIsExpanded } from "modules/portfolio-root/selectors/portfolios"
import { getBasePositionSetWithSegment, getBaseSegmentSignature } from "selectors/base"
import { getSortedContributors, getAvailableRollups, getSelectedGroup, getSortedGroupContributors, getContributorsSummaryText, getCsvData, getSelectedGroupValue, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings } from "./selectors"
import { getDropdownFormatter, getScaledGroupsDropdownColumns, getTableCurrencyScale, getTableFormatter } from "./selectors/scale"
import { getEffectiveSegment } from "modules/analyze-header/selectors/effective-segment"
import { classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getIsOpPro } from "modules/account-root/selectors"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    portfolioId: state.global.selected.portfolio,
    modelId: getSelectedModelId(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSegmentSignature: getBaseSegmentSignature(state, props),
    segment: getEffectiveSegment(state, props),
    classifications: getClassifications(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    expand: getIsExpanded(state, props),
  }),
  required: ["portfolioId", "account"],
  data: [{
    resource: "exposureContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSegmentSignature, contentSetId, segment, classifications, includeNotHeld }) => {
      if (selectedDate) {
        return {
          id: `exposureContributors-${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSegmentSignature}${contentSetId}${JSON.stringify(segment)}${includeNotHeld}`,
          query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $contentSetId: String, $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                exposureContributors (contentSetId: $contentSetId, includeNotHeld: $includeNotHeld) {
                  date
                  contributors {
                    id
                    assetClass
                    assetSubclass
                    notHeld
                    country
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    percentEquity
                    bloombergTicker
                    modelProviderId
                    factors {
                      id
                      category
                      exposure: netContribution
                      currencyExposure
                    }
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            portfolioId,
            date: selectedDate,
            contentSetId,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            segment,
            includeNotHeld,
            expand,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "groupedExposureContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSegmentSignature, contributorsGrouping, contentSetId, segment, classifications, includeNotHeld }) => {
      if (!contributorsGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(contributorsGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `groupedExposureContributors-${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSegmentSignature}${JSON.stringify(queryGroupBy)}${contentSetId}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $contentSetId: String, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                groupedExposureContributors (contentSetId: $contentSetId, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld) {
                  date
                  groupedContributors {
                    name
                    id
                    totalPercentEquity
                    factors {
                      id
                      category
                      exposure: netContribution
                      currencyExposure
                    }
                    contributors {
                      id
                      assetClass
                      notHeld
                      country
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      currency
                      description
                      percentEquity
                      bloombergTicker
                      modelProviderId
                      factors {
                        id
                        category
                        exposure: netContribution
                        currencyExposure
                      }
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          contentSetId,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
        },
      }
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    ...state.modules.historicalExposureContributors,
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    isOpPro: getIsOpPro(state, props),
    showNotHeld: getShowNotHeld(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupBy: getGroupBy(state, props),
    groupsDropdownColumns: getScaledGroupsDropdownColumns(state, props),
    tableCurrencyScale: getTableCurrencyScale(state, props),
    tableFormatter: getTableFormatter(state, props),
    dropdownFormatter: getDropdownFormatter(state, props),
  }),
  actions: {
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
